/**
 * GA4 via the existing gtag.js snippet (Google Ads already loads it in index.html).
 *
 * Measurement ID: VITE_GA4_MEASUREMENT_ID or VITE_FIREBASE_MEASUREMENT_ID.
 * Do not invent a new ID. Do not add a second gtag.js script.
 * Do not send emails, names, document text, signatures, tokens, or payment details.
 */

const INIT_FLAG = '__pdsGa4Init'
const onceKeys = new Set()

const SAFE_EVENT = /^[a-z][a-z0-9_]{1,39}$/
const BLOCKED_PARAM_KEYS = new Set([
  'email',
  'name',
  'phone',
  'token',
  'signature',
  'password',
  'card',
  'document',
  'html',
  'agreement',
  'recipient',
  'address',
  'ip',
  'user_id',
  'uid',
])

export function getGa4MeasurementId() {
  const explicit = String(import.meta.env.VITE_GA4_MEASUREMENT_ID || '').trim()
  const firebase = String(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '').trim()
  const id = explicit || firebase
  return /^G-[A-Z0-9]+$/.test(id) ? id : ''
}

function safeParamValue(value) {
  if (value == null) return undefined
  if (typeof value === 'boolean') return value
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const text = String(value).trim()
  if (!text || text.length > 80) return undefined
  if (/@/.test(text) || /price_/.test(text)) return undefined
  return text
}

function sanitizeParams(params) {
  if (!params || typeof params !== 'object') return {}
  const out = {}
  for (const [key, value] of Object.entries(params)) {
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(key) || BLOCKED_PARAM_KEYS.has(key)) continue
    const safe = safeParamValue(value)
    if (safe !== undefined) out[key] = safe
  }
  return out
}

export function initGa4() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return false
  const id = getGa4MeasurementId()
  if (!id) return false
  if (window[INIT_FLAG]) return true
  window[INIT_FLAG] = true
  window.gtag('config', id, {
    send_page_view: false,
    anonymize_ip: true,
  })
  return true
}

export function trackGa4PageView(pagePath) {
  if (!initGa4()) return
  const path = pagePath || `${window.location.pathname}${window.location.search}`
  if (path.includes('id=') || path.includes('/sign')) return
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  })
}

export function trackGa4Event(name, params) {
  if (!initGa4() || !SAFE_EVENT.test(name)) return
  window.gtag('event', name, sanitizeParams(params))
}

export function trackGa4EventOnce(name, params) {
  if (!SAFE_EVENT.test(name) || onceKeys.has(name)) return
  onceKeys.add(name)
  trackGa4Event(name, params)
}
